import React, { useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Typography,
    Divider,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { clientApi } from "../../api/tenantApi";
import CustomInput from "../../components/common/CustomInput";


// Constants
const DEFAULT_DB_CONFIG = {
    HTTP_HOST: "localhost",
    HTTP_PORT: "8082",
    HTTP_SECURE: false,
    GRPC_HOST: "localhost",
    GRPC_PORT: "50051",
    GRPC_SECURE: false,
};

const PLAN_TYPES = ["Free", "Basic", "Pro", "Enterprise"];

// LLM Providers configuration
const LLM_PROVIDERS = {
    OPENAI: {
        name: "OpenAI",
        models: ["gpt-4", "gpt-4-turbo", "gpt-3.5-turbo"],
    },
    ANTHROPIC: {
        name: "Anthropic (Claude)",
        models: ["claude-3-opus", "claude-3-sonnet", "claude-3-haiku"],
    },
    GOOGLE: {
        name: "Google (Gemini)",
        models: ["gemini-pro", "gemini-pro-vision", "gemini-ultra"],
    },
    COHERE: {
        name: "Cohere",
        models: ["command", "command-light", "command-nightly"],
    },
    MISTRAL: {
        name: "Mistral AI",
        models: ["mistral-large", "mistral-medium", "mistral-small"],
    },
};

// Cloud provider configurations
const CLOUD_PROVIDERS = {
    AWS: {
        name: "AWS",
        authType: "access_key",
        fields: [
            { key: "ACCESS_KEY_ID", label: "Access Key ID", type: "text", required: true },
            { key: "SECRET_ACCESS_KEY", label: "Secret Access Key", type: "password", required: true },
            { key: "REGION", label: "Region", type: "text", required: true, placeholder: "us-east-1" },
            { key: "BUCKET_NAME", label: "S3 Bucket Name", type: "text", required: true },
        ]
    },
    AZURE: {
        name: "Azure",
        authType: "client_secret",
        fields: [
            { key: "CLIENT_ID", label: "Client ID", type: "text", required: true },
            { key: "CLIENT_SECRET", label: "Client Secret", type: "password", required: true },
            { key: "TENANT_ID", label: "Tenant ID", type: "text", required: true },
            { key: "STORAGE_ACCOUNT", label: "Storage Account Name", type: "text", required: true },
            { key: "CONTAINER_NAME", label: "Container Name", type: "text", required: true },
        ]
    },
    GCP: {
        name: "Google Cloud Platform",
        authType: "service_account",
        fields: [
            { key: "PROJECT_ID", label: "Project ID", type: "text", required: true },
            { key: "CLIENT_EMAIL", label: "Service Account Email", type: "text", required: true },
            { key: "PRIVATE_KEY", label: "Private Key", type: "password", required: true },
            { key: "BUCKET_NAME", label: "GCS Bucket Name", type: "text", required: true },
        ]
    },
    SHAREPOINT: {
        name: "SharePoint",
        authType: "oauth2",
        fields: [
            { key: "CLIENT_ID", label: "Client ID", type: "text", required: true },
            { key: "CLIENT_SECRET", label: "Client Secret", type: "password", required: true },
            { key: "TENANT_ID", label: "Tenant ID", type: "text", required: true },
            { key: "SITE_ID", label: "Site ID", type: "text", required: true },
            { key: "DRIVE_ID", label: "Drive ID", type: "text", required: false },
        ]
    },
};


// Validation functions
const validators = {
    companyName: (val) =>
        !val ? "Required." : /^[A-Za-z ]+$/.test(val.trim()) ? "" : "Only alphabets and spaces allowed.",
    companyDomain: (val) =>
        !val ? "Required." : /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/.test(val.trim()) ? "" : "Invalid domain.",
    email: (val) =>
        !val ? "Required." : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()) ? "" : "Invalid email.",
    password: (val) => {
        if (!val) return "Required.";
        if (val.length > 13 || !/[A-Za-z]/.test(val) || !/[0-9]/.test(val) || !/[^A-Za-z0-9]/.test(val)) {
            return "Password must be ≤13 chars, include letters, numbers, and symbols.";
        }
        return "";
    },
    confirmPassword: (pw, cpw) => !cpw ? "Required." : pw === cpw ? "" : "Passwords do not match.",
    planType: (val) => !val ? "Required." : "",
    required: (val) => !val ? "Required." : "",
};

// Initial form state
const getInitialFormState = () => ({
    companyName: "",
    companyDomain: "",
    email: "",
    password: "",
    confirmPassword: "",
    useDbConfig: true,
    dbConfig: { HTTP_HOST: "", HTTP_PORT: "", HTTP_SECURE: false, GRPC_HOST: "", GRPC_PORT: "", GRPC_SECURE: false },
    useDefaultLLMConfig: true,
    llmProvider: "",
    llmModel: "",
    llmApiKey: "",
    useDefaultCloudConfig: true,
    selectedCloudProvider: "",
    cloudConfig: {},
    planType: PLAN_TYPES[0],
});

// Reusable TextField component
const StyledTextField = ({ error, touched, helperText, ...props }) => (
    <CustomInput
        {...props}
        error={error && touched}
        helperText={
            <span style={{
                minHeight: "20px",
                display: "block",
                fontWeight: 500,
                color: "#d32f2f",
                fontSize: "0.7rem",
                marginBottom: 3,
            }}>
                {touched ? helperText : "\u00A0"}
            </span>
        }
        sx={{
            backgroundColor: "#fff",
            mb: 1,
            "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                height: "3rem",
                "& fieldset": {
                    borderColor: "#cbd5e1", // light grey
                },
                "&:hover fieldset": { borderColor: "#000000ff" },
                "&.Mui-focused fieldset": {
                    borderColor: "#000000ff",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                },
            },
            "& .MuiInputLabel-root": {
                fontSize: "0.9em",
                top: "-2px",
                padding: "0",
            },
            ...props.sx,
        }}
    />
);

export default function TenantOnboardingPage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const isSm = useMediaQuery(theme.breakpoints.down("sm"));
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(getInitialFormState());
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

    // Generic field change handler
    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        setTouched((prev) => ({ ...prev, [field]: true }));

        let error = "";
        if (field === "confirmPassword") {
            error = validators.confirmPassword(form.password, value);
        } else if (validators[field]) {
            error = validators[field](value);
        }

        setErrors((prev) => ({ ...prev, [field]: error }));

        // Revalidate confirmPassword when password changes
        if (field === "password" && touched.confirmPassword) {
            setErrors((prev) => ({
                ...prev,
                confirmPassword: validators.confirmPassword(value, form.confirmPassword),
            }));
        }

        // Clear model when provider changes
        if (field === "llmProvider") {
            setForm((prev) => ({ ...prev, llmModel: "" }));
            setErrors((prev) => ({ ...prev, llmModel: "" }));
        }
    };

    const handleBlur = (field) => () => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        let error = "";
        if (field === "confirmPassword") {
            error = validators.confirmPassword(form.password, form[field]);
        } else if (validators[field]) {
            error = validators[field](form[field]);
        }
        setErrors((prev) => ({ ...prev, [field]: error }));
    };

    const handleCheckbox = (field) => (e) => {
        const checked = e.target.checked;

        if (field === "useDbConfig") {
            setForm((prev) => ({
                ...prev,
                useDbConfig: checked,
                dbConfig: checked ? { ...DEFAULT_DB_CONFIG } : { HTTP_HOST: "", HTTP_PORT: "", HTTP_SECURE: false, GRPC_HOST: "", GRPC_PORT: "", GRPC_SECURE: false },
            }));
            setErrors((prev) => {
                const newErr = { ...prev };
                Object.keys(newErr).forEach((k) => k.startsWith("dbConfig.") && delete newErr[k]);
                return newErr;
            });
        } else if (field === "useDefaultLLMConfig") {
            setForm((prev) => ({
                ...prev,
                useDefaultLLMConfig: checked,
                llmProvider: "",
                llmModel: "",
                llmApiKey: "",
            }));
            setErrors((prev) => {
                const newErr = { ...prev };
                delete newErr.llmProvider;
                delete newErr.llmModel;
                delete newErr.llmApiKey;
                return newErr;
            });
        } else if (field === "useDefaultCloudConfig") {
            setForm((prev) => ({
                ...prev,
                useDefaultCloudConfig: checked,
                selectedCloudProvider: "",
                cloudConfig: {},
            }));
            setErrors((prev) => {
                const newErr = { ...prev };
                Object.keys(newErr).forEach((k) => k.startsWith("cloudConfig.") && delete newErr[k]);
                delete newErr.selectedCloudProvider;
                return newErr;
            });
        }
    };

    const handleDbConfigChange = (field) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({
            ...prev,
            dbConfig: { ...prev.dbConfig, [field]: value },
        }));
        setTouched((prev) => ({ ...prev, [`dbConfig.${field}`]: true }));
        setErrors((prev) => ({
            ...prev,
            [`dbConfig.${field}`]: validators.required(value),
        }));
    };

    const handleDbConfigBlur = (field) => () => {
        setTouched((prev) => ({ ...prev, [`dbConfig.${field}`]: true }));
        setErrors((prev) => ({
            ...prev,
            [`dbConfig.${field}`]: validators.required(form.dbConfig[field]),
        }));
    };

    const handleCloudProviderChange = (e) => {
        const provider = e.target.value;
        setForm((prev) => ({
            ...prev,
            selectedCloudProvider: provider,
            cloudConfig: {},
        }));
        setErrors((prev) => {
            const newErr = { ...prev };
            Object.keys(newErr).forEach((k) => k.startsWith("cloudConfig.") && delete newErr[k]);
            return newErr;
        });
        setTouched((prev) => {
            const newTouched = { ...prev };
            Object.keys(newTouched).forEach((k) => k.startsWith("cloudConfig.") && delete newTouched[k]);
            return newTouched;
        });
    };

    const handleCloudConfigChange = (field) => (e) => {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            cloudConfig: { ...prev.cloudConfig, [field]: value },
        }));
        setTouched((prev) => ({ ...prev, [`cloudConfig.${field}`]: true }));
        setErrors((prev) => ({
            ...prev,
            [`cloudConfig.${field}`]: validators.required(value),
        }));
    };

    const handleCloudConfigBlur = (field) => () => {
        setTouched((prev) => ({ ...prev, [`cloudConfig.${field}`]: true }));
        setErrors((prev) => ({
            ...prev,
            [`cloudConfig.${field}`]: validators.required(form.cloudConfig[field]),
        }));
    };

    const validateStep = (stepNum) => {
        let valid = true;
        const newErrors = { ...errors };
        const newTouched = { ...touched };

        if (stepNum === 1) {
            ["companyName", "companyDomain", "email", "password", "confirmPassword"].forEach((f) => {
                newTouched[f] = true;
                const error = f === "confirmPassword"
                    ? validators.confirmPassword(form.password, form[f])
                    : validators[f](form[f]);
                newErrors[f] = error;
                if (error) valid = false;
            });
        } else if (stepNum === 2) {
            if (!form.useDbConfig) {
                ["HTTP_HOST", "HTTP_PORT", "GRPC_HOST", "GRPC_PORT"].forEach((f) => {
                    newTouched[`dbConfig.${f}`] = true;
                    const error = validators.required(form.dbConfig[f]);
                    newErrors[`dbConfig.${f}`] = error;
                    if (error) valid = false;
                });
            }
            if (!form.useDefaultLLMConfig) {
                ["llmProvider", "llmModel", "llmApiKey"].forEach((f) => {
                    newTouched[f] = true;
                    const error = validators.required(form[f]);
                    newErrors[f] = error;
                    if (error) valid = false;
                });
            }
        } else if (stepNum === 3) {
            newTouched.planType = true;
            const planError = validators.planType(form.planType);
            newErrors.planType = planError;
            if (planError) valid = false;

            if (!form.useDefaultCloudConfig) {
                if (!form.selectedCloudProvider) {
                    newTouched.selectedCloudProvider = true;
                    newErrors.selectedCloudProvider = "Please select a cloud provider.";
                    valid = false;
                } else {
                    const provider = CLOUD_PROVIDERS[form.selectedCloudProvider];
                    if (provider) {
                        provider.fields.forEach((field) => {
                            if (field.required) {
                                const value = form.cloudConfig[field.key];
                                newTouched[`cloudConfig.${field.key}`] = true;
                                const error = validators.required(value);
                                newErrors[`cloudConfig.${field.key}`] = error;
                                if (error) valid = false;
                            }
                        });
                    }
                }
            }
        }

        setTouched(newTouched);
        setErrors(newErrors);
        return valid;
    };

    const handleNext = (e) => {
        e.preventDefault();
        if (validateStep(step)) {
            setStep((s) => s + 1);
        }
    };

    const handleBack = () => setStep((s) => s - 1);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
            setSnackbar({ open: true, message: "Please fix all errors before submitting.", type: "error" });
            return;
        }

        setLoading(true);

        const payload = {
            company_name: form.companyName.trim(),
            company_domain: form.companyDomain.trim(),
            email: form.email.trim(),
            password: form.password,
            user_tier: form.planType,
            user_type: "recruiter",
            no_of_question_per_interview: 5,
            no_of_question_per_skill: 5,
        };

        // DB Config
        if (form.useDbConfig) {
            payload.is_use_default_db_configuration = true;
        } else {
            payload.is_use_default_db_configuration = false;
            payload.http_hostname = form.dbConfig.HTTP_HOST;
            payload.http_port = form.dbConfig.HTTP_PORT;
            payload.grpc_hostname = form.dbConfig.GRPC_HOST;
            payload.grpc_port = form.dbConfig.GRPC_PORT;
        }

        // LLM Config
        if (form.useDefaultLLMConfig) {
            payload.is_use_default_llm_config = true;
        } else {
            payload.is_use_default_llm_config = false;
            payload.llm_name_provider = form.llmProvider;
            payload.llm_model = form.llmModel;
            payload.llm_api_keys = form.llmApiKey;
        }

        // Cloud Config
        if (form.useDefaultCloudConfig) {
            payload.is_use_default_cloud_config = true;
        } else {
            payload.is_use_default_cloud_config = false;
            const providerConfig = CLOUD_PROVIDERS[form.selectedCloudProvider];
            if (providerConfig) {
                payload.cloud_provider = form.selectedCloudProvider.toLowerCase();
                payload.auth_type = providerConfig.authType;

                if (form.cloudConfig.REGION) {
                    payload.region = form.cloudConfig.REGION;
                }

                payload.cloud_config = { ...form.cloudConfig };
            }
        }

        try {
            const response = await clientApi.onboardClient(payload);
            // console.log("Onboard client response:", response);

            const { status, message, data } = response || {};

            // 1️⃣ Network error
            if (status === 0) {
                setSnackbar({
                    open: true,
                    message: "Network error. Please check your connection.",
                    severity: "warning",
                });
                return;
            }

            // 2️⃣ Validation / client errors
            if (status === 400) {
                setSnackbar({
                    open: true,
                    message: message || "Invalid onboarding request.",
                    severity: "warning",
                });
                return;
            }

            // 3️⃣ Server errors
            if (status >= 500) {
                setSnackbar({
                    open: true,
                    message: message || "Server error while onboarding tenant.",
                    severity: "error",
                });
                return;
            }

            // 4️⃣ Success
            if (status === 201) {
                setSnackbar({
                    open: true,
                    message: message || "Tenant onboarded successfully!",
                    severity: "success",
                });

                // Optional: you can use recruiter_id if needed
                // console.log("New recruiter ID:", data?.recruiter_id);

                setForm(getInitialFormState());
                setErrors({});
                setTouched({});
                setStep(1);

                navigate("/tenant/management");
                return;
            }
            // 5️⃣ Fallback
            setSnackbar({
                open: true,
                message: "Unexpected response from server.",
                severity: "error",
            });
        } catch (err) {
            console.error("Onboard tenant failed:", err);
            setSnackbar({
                open: true,
                message: "Something went wrong. Please try again.",
                severity: "error",
            })
        } finally {
            setLoading(false);
        }
    };

    const renderStep1 = () => (
        <>
            <Typography variant="h1" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.5rem", mb: 3 }}>
                Company Info
            </Typography>

            <StyledTextField
                label="Company Name"
                fullWidth
                required
                value={form.companyName}
                onChange={handleChange("companyName")}
                onBlur={handleBlur("companyName")}
                error={!!errors.companyName}
                touched={touched.companyName}
                helperText={errors.companyName}
            />

            {/* <StyledTextField
                label="Company Domain"
                fullWidth
                required
                value={form.companyDomain}
                onChange={handleChange("companyDomain")}
                onBlur={handleBlur("companyDomain")}
                error={!!errors.companyDomain}
                touched={touched.companyDomain}
                helperText={errors.companyDomain}
                placeholder="example.com"
            /> */}

            <StyledTextField
                label="Email ID"
                fullWidth
                required
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                onBlur={handleBlur("email")}
                error={!!errors.email}
                touched={touched.email}
                helperText={errors.email}
            />

            <StyledTextField
                label="Password"
                fullWidth
                required
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange("password")}
                onBlur={handleBlur("password")}
                error={!!errors.password}
                touched={touched.password}
                helperText={errors.password}
                inputProps={{ maxLength: 13 }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Button onClick={() => setShowPassword((v) => !v)} tabIndex={-1} size="small">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </Button>
                        </InputAdornment>
                    ),
                }}
            />

            <StyledTextField
                label="Confirm Password"
                fullWidth
                required
                type={showConfirmPassword ? "text" : "password"}
                value={form.confirmPassword}
                onChange={handleChange("confirmPassword")}
                onBlur={handleBlur("confirmPassword")}
                error={!!errors.confirmPassword}
                touched={touched.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Button onClick={() => setShowConfirmPassword((v) => !v)} tabIndex={-1} size="small">
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                            </Button>
                        </InputAdornment>
                    ),
                }}
            />
        </>
    );

    const renderStep2 = () => (
        <>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.3rem", mb: 2 }}>
                Database Configuration
            </Typography>

            <FormControlLabel
                control={<Checkbox checked={form.useDbConfig} onChange={handleCheckbox("useDbConfig")} />}
                label="Use Default Database Configuration"
                sx={{ mb: 2 }}
            />

            {!form.useDbConfig && (
                <>
                    {[
                        { label: "HTTP Host", key: "HTTP_HOST", placeholder: "localhost" },
                        { label: "HTTP Port", key: "HTTP_PORT", placeholder: "8082" },
                        { label: "GRPC Host", key: "GRPC_HOST", placeholder: "localhost" },
                        { label: "GRPC Port", key: "GRPC_PORT", placeholder: "50051" },
                    ].map(({ label, key, placeholder }) => (
                        <StyledTextField
                            key={key}
                            label={label}
                            fullWidth
                            placeholder={placeholder}
                            value={form.dbConfig[key]}
                            onChange={handleDbConfigChange(key)}
                            onBlur={handleDbConfigBlur(key)}
                            error={!!errors[`dbConfig.${key}`]}
                            touched={touched[`dbConfig.${key}`]}
                            helperText={errors[`dbConfig.${key}`]}
                        />
                    ))}
                </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.3rem", mb: 2 }}>
                LLM Configuration
            </Typography>

            <FormControlLabel
                control={<Checkbox checked={form.useDefaultLLMConfig} onChange={handleCheckbox("useDefaultLLMConfig")} />}
                label="Use Default LLM Configuration"
                sx={{ mb: 2 }}
            />

            {!form.useDefaultLLMConfig && (
                <>
                    <StyledTextField
                        label="LLM Provider"
                        fullWidth
                        select
                        required
                        value={form.llmProvider}
                        onChange={handleChange("llmProvider")}
                        onBlur={handleBlur("llmProvider")}
                        error={!!errors.llmProvider}
                        touched={touched.llmProvider}
                        helperText={errors.llmProvider}
                    >
                        <MenuItem value="">Select Provider</MenuItem>
                        {Object.keys(LLM_PROVIDERS).map((key) => (
                            <MenuItem key={key} value={key}>
                                {LLM_PROVIDERS[key].name}
                            </MenuItem>
                        ))}
                    </StyledTextField>

                    {form.llmProvider && (
                        <StyledTextField
                            label="LLM Model"
                            fullWidth
                            select
                            required
                            value={form.llmModel}
                            onChange={handleChange("llmModel")}
                            onBlur={handleBlur("llmModel")}
                            error={!!errors.llmModel}
                            touched={touched.llmModel}
                            helperText={errors.llmModel}
                        >
                            <MenuItem value="">Select Model</MenuItem>
                            {LLM_PROVIDERS[form.llmProvider].models.map((model) => (
                                <MenuItem key={model} value={model}>
                                    {model}
                                </MenuItem>
                            ))}
                        </StyledTextField>
                    )}

                    <StyledTextField
                        label="API Key"
                        fullWidth
                        required
                        type="password"
                        placeholder="Enter your LLM API key"
                        value={form.llmApiKey}
                        onChange={handleChange("llmApiKey")}
                        onBlur={handleBlur("llmApiKey")}
                        error={!!errors.llmApiKey}
                        touched={touched.llmApiKey}
                        helperText={errors.llmApiKey}
                    />
                </>
            )}
        </>
    );

    const renderStep3 = () => (
        <>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.3rem", mb: 2 }}>
                Cloud Configuration
            </Typography>

            <FormControlLabel
                control={<Checkbox checked={form.useDefaultCloudConfig} onChange={handleCheckbox("useDefaultCloudConfig")} />}
                label="Use Default Cloud Configuration"
                sx={{ mb: 2 }}
            />

            {!form.useDefaultCloudConfig && (
                <>
                    <StyledTextField
                        label="Cloud Provider"
                        fullWidth
                        select
                        required
                        value={form.selectedCloudProvider}
                        onChange={handleCloudProviderChange}
                        error={!!errors.selectedCloudProvider}
                        touched={touched.selectedCloudProvider}
                        helperText={errors.selectedCloudProvider}
                    >
                        <MenuItem value="">Select Provider</MenuItem>
                        {Object.keys(CLOUD_PROVIDERS).map((key) => (
                            <MenuItem key={key} value={key}>
                                {CLOUD_PROVIDERS[key].name}
                            </MenuItem>
                        ))}
                    </StyledTextField>

                    {form.selectedCloudProvider && CLOUD_PROVIDERS[form.selectedCloudProvider] && (
                        <Box >
                            <Typography variant="body2" sx={{ color: "#64748b", mb: 2, fontWeight: 600 }}>
                                {CLOUD_PROVIDERS[form.selectedCloudProvider].name} Configuration
                            </Typography>
                            {CLOUD_PROVIDERS[form.selectedCloudProvider].fields.map((field) => (
                                <StyledTextField
                                    key={field.key}
                                    label={field.label}
                                    fullWidth
                                    required={field.required}
                                    type={field.type || "text"}
                                    placeholder={field.placeholder || ""}
                                    value={form.cloudConfig[field.key] || ""}
                                    onChange={handleCloudConfigChange(field.key)}
                                    onBlur={handleCloudConfigBlur(field.key)}
                                    error={!!errors[`cloudConfig.${field.key}`]}
                                    touched={touched[`cloudConfig.${field.key}`]}
                                    helperText={errors[`cloudConfig.${field.key}`]}
                                />
                            ))}
                        </Box>
                    )}
                </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: "#1e293b", fontSize: "1.3rem", mb: 2 }}>
                Plan Type
            </Typography>

            <FormControl error={!!errors.planType && touched.planType} sx={{ backgroundColor: "#fff", borderRadius: "16px", p: 1 }}>
                <RadioGroup row value={form.planType} onChange={handleChange("planType")} onBlur={handleBlur("planType")}>
                    {PLAN_TYPES.map((type) => (
                        <FormControlLabel
                            key={type}
                            value={type}
                            control={<Radio />}
                            label={type}
                            sx={{ "& .MuiTypography-root": { fontWeight: 600 } }}
                        />
                    ))}
                </RadioGroup>
                <FormHelperText>
                    <span style={{ minHeight: "20px", display: "block", fontWeight: 500, color: "#d32f2f", fontSize: "0.7rem", marginBottom: 2 }}>
                        {touched.planType ? errors.planType : "\u00A0"}
                    </span>
                </FormHelperText>
            </FormControl>
        </>
    );

    return (

        <Box sx={{ display: "flex", background: "linear-gradient(135deg, #e5e7eb, #7f858fff, #151618ff)", minHeight: "100vh" }}>
            {/* Form */}
            <Box sx={{ flex: 1, background: "white", p: isSm ? 1 : 5, display: "flex", alignItems: "top", justifyContent: "center" }}>
                <Box sx={{ width: "60%", maxWidth: 900 }}>
                    {/* Progress Stepper */}
                    <Box sx={{ display: "flex", gap: 2, mb: 3, width: "100%" }}>
                        {[1, 2, 3].map((s) => (
                            <Box key={s} sx={{
                                flex: 1, height: 8, borderRadius: 4,
                                background: step >= s ? "#1e293b" : "#e5e7eb",
                                transition: "background 0.2s",
                            }} />
                        ))}
                    </Box>

                    <form onSubmit={handleSubmit} autoComplete="off">
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}

                        {/* Navigation Buttons */}
                        <Divider sx={{ mt: 2, mb: 1 }} />
                        <Box mt={4} display="flex" justifyContent="space-between" gap={2}>
                            {step > 1 && (
                                <Button
                                    variant="outlined"
                                    disabled={loading}
                                    onClick={handleBack}
                                    sx={{
                                        backgroundColor: "#fb4747ff",
                                        color: "white",
                                        borderRadius: "14px",
                                        fontWeight: 700,
                                        fontSize: "1rem",
                                        "&:hover": { backgroundColor: "#a90000ff" }
                                    }}
                                >
                                    Back
                                </Button>
                            )}
                            {step < 3 ? (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: "#1e293b",
                                        color: "white",
                                        borderRadius: "14px",
                                        fontWeight: 700,
                                        fontSize: "1rem",
                                        "&:hover": { backgroundColor: "#000000" }
                                    }}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    type="submit"
                                    disabled={loading}
                                    sx={{
                                        backgroundColor: "#1e293b",
                                        color: "white",
                                        borderRadius: "14px",
                                        fontWeight: 700,
                                        fontSize: "1rem",
                                        "&:hover": { backgroundColor: "#000000" }
                                    }}
                                >
                                    {loading ? "Submitting..." : "Submit"}
                                </Button>
                            )}
                        </Box>
                    </form>


                </Box>
                {/* <CustomSnackBar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                autoHideDuration={4000}
            />  */}
            </Box>
        </Box>
    )
}